import { jsx as _jsx } from "react/jsx-runtime";
import { ShortUrl } from "@air/api";
import { UploadDropzone } from "@air/component-upload-dropzone";
import { matchesAirror, NO_MORE_STORAGE, SHARE_OBJECT_NOT_FOUND } from "@air/errors";
import { ContentCollectionView, setViewAction } from "@air/feature-content-collection/redux";
import { useAirModal } from "@air/provider-modal";
import { useShortIdContext } from "@air/provider-short-id";
import { useToasts } from "@air/provider-toast";
import { queueUploadsAction, rejectUploadsAction } from "@air/redux-uploader";
import { createUpload } from "@air/upload-utils";
import uniqueId from "lodash/uniqueId";
import { memo } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { OutOfStorageModal } from "~/components/BoardCollection/OutOfStorageModal";
import { getSortedFilesAndMetadata } from "~/components/BoardCollection/utils";
import { setBoardCollectionViewAction } from "~/store/boardCollection/actions";
import { boardCollectionHasSubmittedFormSelector } from "~/store/boardCollection/selectors";
import { setPublicUploadMetadata } from "~/store/publicUploadMetadata/actions";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
import { sanitizeRestrictedPathCharsInFile } from "~/utils/FileUtils";
import { MissingFileExtensionsModal } from "../Modals/MissingFileExtenionsModal";
import { RestrictedCharacterReplacementModal } from "../Modals/RestrictedCharacterReplacementModal";
import { useGetBoardCollectionSessionId } from "./useGetBoardCollectionSessionId";
export const BoardCollectionDropzone = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    const { shortId } = useShortIdContext();
    const { getSessionId } = useGetBoardCollectionSessionId();
    const [showOutOfStorageModal] = useAirModal(OutOfStorageModal);
    const [showMissingFileExtensionsModal] = useAirModal(MissingFileExtensionsModal);
    const [showRestrictedCharacterConfirmationModal] = useAirModal(RestrictedCharacterReplacementModal);
    const { showToast } = useToasts();
    const dispatch = useDispatch();
    const hasSubmitted = useSelector(boardCollectionHasSubmittedFormSelector);
    const startUpload = async (param)=>{
        let { acceptedFiles, rejectedFiles } = param;
        const { sessionId } = await getSessionId();
        const { emptyFiles, filesWithNoExtension, filesWithRestrictedChars, files, totalSize } = getSortedFilesAndMetadata(acceptedFiles);
        let canUpload = false;
        let failureReason = undefined;
        try {
            const response = await ShortUrl.checkCanUpload(shortId, totalSize);
            canUpload = response.canUpload;
            failureReason = response.failureReason;
        } catch (error) {
            if (matchesAirror(error, SHARE_OBJECT_NOT_FOUND)) {
                showToast("The share link you’re trying to upload to is no longer available", {
                    color: "red"
                });
            } else {
                showToast("Something went wrong. Please try again later.", {
                    color: "red"
                });
            }
            return;
        }
        if (!canUpload) {
            switch(failureReason){
                case NO_MORE_STORAGE.message:
                    return showOutOfStorageModal();
                default:
                    {
                        reportErrorToBugsnag({
                            error: new Error("Uploading to a public dropzone failed"),
                            context: "Uploading to a public dropzone failed",
                            metadata: {
                                data: {
                                    failureReason,
                                    shortId
                                }
                            }
                        });
                        return showToast("Something went wrong. Please try again later.");
                    }
            }
        }
        // If there are any files without extensions, prompt the user to cancel or continue without those files.
        if (filesWithNoExtension.length) {
            const shouldCancelUpload = await new Promise((resolve)=>{
                showMissingFileExtensionsModal({
                    items: filesWithNoExtension,
                    onCancel: ()=>{
                        resolve(true);
                    },
                    onConfirm: ()=>{
                        resolve(false);
                    }
                });
            });
            if (shouldCancelUpload) {
                return;
            }
        }
        // If there are any file with names that contain restricted characters, prompt the user to confirm the replacement of these characters.
        if (filesWithRestrictedChars.length) {
            const { confirmed } = await new Promise((resolve)=>{
                showRestrictedCharacterConfirmationModal({
                    items: filesWithRestrictedChars,
                    onCancel: ()=>{
                        resolve({
                            confirmed: false
                        });
                    },
                    onConfirm: ()=>{
                        resolve({
                            confirmed: true
                        });
                    }
                });
            });
            if (!confirmed) {
                return;
            } else {
                const sanitizedFiles = filesWithRestrictedChars.map((file)=>sanitizeRestrictedPathCharsInFile(file));
                files.push(...sanitizedFiles);
            }
        }
        if (files.length) {
            const batchId = uniqueId("batch");
            const uploads = files.map((file)=>createUpload({
                    file,
                    batchId
                }));
            batch(()=>{
                dispatch(queueUploadsAction({
                    uploads
                }));
                dispatch(setBoardCollectionViewAction({
                    view: "manage"
                }));
                dispatch(setViewAction({
                    view: ContentCollectionView.Manage
                }));
                uploads.forEach((upload)=>dispatch(setPublicUploadMetadata({
                        uploadId: upload.id,
                        metadata: {
                            sessionId,
                            shortBoardId: shortId
                        }
                    })));
            });
        }
        if (rejectedFiles.length + emptyFiles.length + filesWithNoExtension.length > 0) {
            const mapWithReason = (reason)=>(files)=>files.map((param)=>{
                        let { name } = param;
                        return {
                            name,
                            reason
                        };
                    });
            dispatch(rejectUploadsAction({
                files: [
                    ...mapWithReason("unsupported")(rejectedFiles),
                    ...mapWithReason("empty")(emptyFiles),
                    ...mapWithReason("noExtension")(filesWithNoExtension)
                ]
            }));
        }
    };
    return /*#__PURE__*/ _jsx(UploadDropzone, {
        disabled: hasSubmitted,
        onUpload: startUpload,
        children: children
    });
});
BoardCollectionDropzone.displayName = "BoardCollectionDropzone";
